import type { MultiValue, Option } from '@ksyos/design-system';
import type { JSX } from 'react';
import type { ActionMeta } from 'react-select';
import { Alert, Form, FormGroup, FormSection, Select } from '@ksyos/design-system';
import { useState } from 'react';
import type { ServiceOrganizationRole as Role } from '@common/enums';
import type { ServiceOrganizationSettingRole } from '@common/interfaces/api/serviceOrganizationSettings';
import { getSelectedLanguage } from '@frontend/translate';

type ServiceOrganizationRolesEditProps = {
    currentServiceOrganizationSettingRoles?: ServiceOrganizationSettingRole[];
    errors: Map<Role, string>;
    onChange: (options: MultiValue<Option>) => Promise<void>;
    roles: Role[];
};

export function ServiceOrganizationRolesEdit(props: ServiceOrganizationRolesEditProps): JSX.Element {
    const { currentServiceOrganizationSettingRoles, onChange, roles } = props;
    const [values, setValues] = useState<Option[] | undefined>(getDefaultValues());
    const languageFile = getSelectedLanguage();

    return (
        <Form submitOnEnter={false}>
            <FormSection>
                <FormGroup label={languageFile.serviceOrganizationSetting['organizationRole(s)']}>
                    <Select
                        isClearable={true}
                        isMulti={true}
                        noOptionsMessage={() => languageFile.common.noOptionsAvailable}
                        onChange={handleChange}
                        options={roles.map((role) => ({
                            label: languageFile.serviceOrganizationRoleEnum[role],
                            value: role,
                        }))}
                        value={values}
                    />
                </FormGroup>
                {props.errors.size > 0 &&
                    props.roles
                        .filter((role) => props.errors.has(role))
                        .map((role) => (
                            <Alert
                                appearance="warning"
                                hasIcon={true}
                                key={role}
                            >
                                {props.errors.get(role)}
                            </Alert>
                        ))}
            </FormSection>
        </Form>
    );

    function mapServiceOrganizationRoleToOption(
        serviceOrganizationSettingRole: ServiceOrganizationSettingRole
    ): Option {
        return {
            label: getSelectedLanguage().serviceOrganizationRoleEnum[serviceOrganizationSettingRole.role],
            value: serviceOrganizationSettingRole.role,
        };
    }

    function getDefaultValues(): Option[] | undefined {
        if (!currentServiceOrganizationSettingRoles) {
            return;
        }

        return currentServiceOrganizationSettingRoles.map(mapServiceOrganizationRoleToOption);
    }

    async function handleChange(options: MultiValue<Option>, actionMeta: ActionMeta<Option>): Promise<void> {
        if (actionMeta.action === 'select-option' && actionMeta?.option?.value) {
            const nextValues = [...(values ?? []), actionMeta.option];
            setValues(nextValues);

            return await onChange(options);
        }
        if (actionMeta.action === 'remove-value' && actionMeta?.removedValue?.value) {
            const nextValues = values?.filter((value) => value !== actionMeta.removedValue);
            setValues(nextValues);

            return await onChange(options);
        }
        if (actionMeta.action === 'clear') {
            setValues([]);

            return await onChange([]);
        }
    }
}
